import { Link } from "react-router-dom";
import { Lock, EyeOff, Globe, CalendarDays, MapPin } from "lucide-react";
import { useI18n, EVENT_TYPES, labelOf } from "@/lib/i18n";
import SmartImage from "@/components/SmartImage";

const PRIVACY = {
  public: { icon: Globe, key: "privacy.public", cls: "text-[#275E64] border-[#478D92]" },
  password: { icon: Lock, key: "privacy.password", cls: "text-[#8F3329] border-[#C69A3B]" },
  private: { icon: EyeOff, key: "privacy.private", cls: "text-[#8F3329] border-[#8F3329]" },
};

export const PrivacyBadge = ({ privacy }) => {
  const { t } = useI18n();
  const conf = PRIVACY[privacy] || PRIVACY.public;
  const Icon = conf.icon;
  return (
    <span
      data-testid={`privacy-badge-${privacy}`}
      className={`inline-flex items-center gap-1.5 border bg-[#FAFAF7]/90 px-2.5 py-1 text-[11px] font-medium tracking-wide ${conf.cls}`}
    >
      <Icon size={12} /> {t(conf.key)}
    </span>
  );
};

export default function EventCard({ event }) {
  const { t, path, localized, formatDate, lang } = useI18n();
  const name = localized(event, "name");
  const desc = localized(event, "description");
  const cover = event.cover_image;

  return (
    <article
      data-testid={`event-card-${event.slug}`}
      className="group flex flex-col overflow-hidden border border-[#C69A3B]/30 bg-white transition-shadow duration-500 hover:shadow-[0_18px_50px_-30px_rgba(37,37,37,0.5)]"
    >
      <Link to={path("events", event.slug)} className="photo-hover relative block aspect-[4/3] overflow-hidden bg-[#D8D3CB]">
        {cover ? (
          <SmartImage src={cover} alt={name} loading="lazy" className="h-full w-full" />
        ) : event.privacy === "public" ? (
          <div className="grid h-full w-full place-items-center bg-[#E8D8B6]">
            <span className="font-serif-display text-2xl text-[#8F3329]">HR</span>
          </div>
        ) : (
          <div className="grid h-full w-full place-items-center bg-[#252525]/90 text-center">
            <span className="px-6 text-xs text-[#E8D8B6]">
              {event.privacy === "private" ? t("privacy.noticePrivate") : t("privacy.noticePassword")}
            </span>
          </div>
        )}
        <span className="absolute left-3 top-3">
          <PrivacyBadge privacy={event.privacy} />
        </span>
      </Link>
      <div className="flex flex-1 flex-col gap-3 p-6">
        <p className="label-mono">{labelOf(EVENT_TYPES, event.event_type, lang)}</p>
        <h3 className="font-serif-display text-lg sm:text-xl leading-snug text-[#252525]">{name}</h3>
        <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-[#5A5752]">
          <span className="inline-flex items-center gap-1.5">
            <CalendarDays size={13} className="text-[#C69A3B]" /> {formatDate(event.event_date)}
          </span>
          {event.location && (
            <span className="inline-flex items-center gap-1.5">
              <MapPin size={13} className="text-[#C69A3B]" /> {event.location}
            </span>
          )}
        </div>
        {desc && <p className="line-clamp-3 text-sm text-[#5A5752]">{desc}</p>}
        <Link
          to={path("events", event.slug)}
          data-testid={`btn-view-gallery-${event.slug}`}
          className="mt-auto inline-flex min-h-[44px] w-fit items-center border border-[#C69A3B] px-5 text-sm text-[#8F3329] transition-colors duration-300 hover:bg-[#8F3329] hover:text-[#FAFAF7]"
        >
          {t("btn.viewGallery")}
        </Link>
      </div>
    </article>
  );
}
